import { useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import EmojiPicker from "emoji-picker-react";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Unlink,
  MousePointerClick,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Smile,
  RemoveFormatting,
  Undo2,
  Redo2,
  Minus,
} from "lucide-react";

// ============ Toolbar button primitive ============
const ToolBtn = ({ onClick, active, title, disabled, children, testid }) => (
  <button
    type="button"
    onClick={(e) => { e.preventDefault(); onClick(); }}
    disabled={disabled}
    title={title}
    aria-label={title}
    data-testid={testid}
    className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
      active
        ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
        : "text-[hsl(var(--onyx)/0.7)] hover:bg-[hsl(var(--blush)/0.3)] hover:text-[hsl(var(--onyx))]"
    }`}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-6 bg-[hsl(var(--onyx)/0.1)] mx-1" />;

// ============ Rich-text email composer ============
export default function RichTextEmailComposer({ value, onChange, placeholder }) {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showCTAModal, setShowCTAModal] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const emojiRef = useRef(null);
  const wrapperRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Allow multiple <p> tags in a row (line spacing = extra empty paragraphs).
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          // Empty target so the style-inliner treats generic links normally
          // (CTAs use a separate custom node via data-cta attribute).
          rel: "noopener noreferrer",
        },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({
        placeholder: placeholder || "Write your email here — bold, italic, headings, lists, links, buttons, emojis. Paste from ChatGPT is fine.",
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[380px] px-6 py-5 bg-white text-[hsl(var(--onyx))]",
        "data-testid": "email-editor-area",
      },
    },
  });

  // Keep external value in sync (e.g. loading a template)
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // Close emoji picker on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
    };
    if (showEmoji) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showEmoji]);

  const insertLink = (url, text) => {
    if (!url) return;
    const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    if (text) {
      editor
        .chain()
        .focus()
        .insertContent(`<a href="${safe}">${text}</a>`)
        .run();
    } else {
      // wrap current selection
      editor.chain().focus().extendMarkRange("link").setLink({ href: safe }).run();
    }
    setShowLinkModal(false);
  };

  const insertCTA = (url, label) => {
    if (!url || !label) return;
    const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    // Custom marker <a data-cta="button"> — backend converts to a table-based
    // bulletproof pill button at send-time so Outlook renders correctly.
    editor
      .chain()
      .focus()
      .insertContent(
        `<p><a data-cta="button" href="${safe}">${label}</a></p>`
      )
      .run();
    setShowCTAModal(false);
  };

  const currentLink = editor?.getAttributes("link")?.href || "";

  if (!editor) return <div className="p-10 text-center text-[hsl(var(--onyx)/0.4)]">Loading editor…</div>;

  return (
    <div
      ref={wrapperRef}
      className="rounded-2xl bg-white border border-[hsl(var(--onyx)/0.15)] overflow-visible"
      data-testid="rich-email-composer"
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-[hsl(var(--onyx)/0.08)] bg-[hsl(var(--cream)/0.6)] sticky top-0 z-10 rounded-t-2xl">
        <ToolBtn testid="fmt-bold" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
          <Bold size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-italic" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
          <Italic size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-underline" onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline">
          <UnderlineIcon size={16} />
        </ToolBtn>

        <Sep />

        <ToolBtn testid="fmt-h1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1">
          <Heading1 size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-h2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2">
          <Heading2 size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-h3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3">
          <Heading3 size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-p" onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} title="Paragraph">
          P
        </ToolBtn>

        <Sep />

        <ToolBtn testid="fmt-ul" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list">
          <List size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-ol" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list">
          <ListOrdered size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-quote" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote">
          <Quote size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-hr" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider">
          <Minus size={16} />
        </ToolBtn>

        <Sep />

        <ToolBtn testid="fmt-left" onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left">
          <AlignLeft size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-center" onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center">
          <AlignCenter size={16} />
        </ToolBtn>
        <ToolBtn testid="fmt-right" onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right">
          <AlignRight size={16} />
        </ToolBtn>

        <Sep />

        <ToolBtn testid="fmt-link" onClick={() => setShowLinkModal(true)} active={editor.isActive("link")} title="Insert link">
          <LinkIcon size={16} />
        </ToolBtn>
        {editor.isActive("link") && (
          <ToolBtn testid="fmt-unlink" onClick={() => editor.chain().focus().unsetLink().run()} title="Remove link">
            <Unlink size={16} />
          </ToolBtn>
        )}
        <ToolBtn testid="fmt-cta" onClick={() => setShowCTAModal(true)} title="Insert call-to-action button">
          <MousePointerClick size={16} />
        </ToolBtn>

        <Sep />

        <div ref={emojiRef} className="relative">
          <ToolBtn testid="fmt-emoji" onClick={() => setShowEmoji(!showEmoji)} active={showEmoji} title="Emoji">
            <Smile size={16} />
          </ToolBtn>
          {showEmoji && (
            <div className="absolute top-11 left-0 z-30 shadow-2xl rounded-2xl overflow-hidden">
              <EmojiPicker
                onEmojiClick={(emojiData) => {
                  editor.chain().focus().insertContent(emojiData.emoji).run();
                  setShowEmoji(false);
                }}
                width={340}
                height={380}
                previewConfig={{ showPreview: false }}
              />
            </div>
          )}
        </div>

        <Sep />

        <ToolBtn testid="fmt-clear" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting">
          <RemoveFormatting size={16} />
        </ToolBtn>

        <div className="ml-auto flex items-center gap-1">
          <ToolBtn testid="fmt-undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
            <Undo2 size={16} />
          </ToolBtn>
          <ToolBtn testid="fmt-redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
            <Redo2 size={16} />
          </ToolBtn>
        </div>
      </div>

      {/* Editor */}
      <div className="tiptap-body">
        <EditorContent editor={editor} />
      </div>

      {/* Link modal */}
      {showLinkModal && (
        <LinkModal
          onClose={() => setShowLinkModal(false)}
          onInsert={insertLink}
          initialUrl={currentLink}
          hasSelection={editor.state.selection.from !== editor.state.selection.to}
        />
      )}
      {/* CTA modal */}
      {showCTAModal && (
        <CTAModal onClose={() => setShowCTAModal(false)} onInsert={insertCTA} />
      )}

      {/* Global styles for editor content — matches email brand look */}
      <style>{`
        .tiptap-body .ProseMirror { min-height: 380px; }
        .tiptap-body .ProseMirror:focus { outline: none; }
        .tiptap-body .ProseMirror p.is-editor-empty:first-child::before {
          color: hsl(var(--onyx)/0.35);
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .tiptap-body h1 { font-family: Georgia, serif; font-weight: 400; font-size: 28px; margin: 20px 0 12px; line-height: 1.2; color: hsl(var(--onyx)); }
        .tiptap-body h2 { font-family: Georgia, serif; font-weight: 400; font-size: 22px; margin: 18px 0 10px; line-height: 1.25; color: hsl(var(--onyx)); }
        .tiptap-body h3 { font-family: Georgia, serif; font-weight: 500; font-size: 18px; margin: 16px 0 8px; line-height: 1.3; color: hsl(var(--onyx)); }
        .tiptap-body p { font-size: 15px; line-height: 1.7; margin: 0 0 14px; color: hsl(var(--onyx)/0.82); }
        .tiptap-body ul, .tiptap-body ol { padding-left: 22px; margin: 0 0 16px; }
        .tiptap-body ul li, .tiptap-body ol li { margin: 0 0 6px; }
        .tiptap-body ul { list-style: disc; }
        .tiptap-body ol { list-style: decimal; }
        .tiptap-body a { color: hsl(var(--burgundy)); text-decoration: underline; }
        .tiptap-body a[data-cta="button"] {
          display: inline-block; padding: 12px 28px; background: hsl(var(--onyx));
          color: hsl(var(--cream)) !important; text-decoration: none; border-radius: 9999px;
          font-size: 13px; letter-spacing: 0.02em; margin: 4px 0;
        }
        .tiptap-body blockquote { border-left: 3px solid hsl(var(--gold)); padding: 6px 14px; margin: 0 0 16px; color: hsl(var(--onyx)/0.65); font-style: italic; }
        .tiptap-body hr { border: 0; border-top: 1px solid hsl(var(--onyx)/0.08); margin: 20px 0; }
        .tiptap-body strong { font-weight: 600; color: hsl(var(--onyx)); }
      `}</style>
    </div>
  );
}

// ============ Link modal ============
function LinkModal({ onClose, onInsert, initialUrl, hasSelection }) {
  const [url, setUrl] = useState(initialUrl || "");
  const [text, setText] = useState("");
  return (
    <ModalShell onClose={onClose} title="Insert link">
      <div className="space-y-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">URL</span>
          <input
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://malekdridihairandbeauty.com/shop"
            data-testid="link-modal-url"
            className="mt-1.5 w-full h-11 rounded-2xl bg-white border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
          />
        </label>
        {!hasSelection && (
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">
              Link text (optional — leave blank to just paste the URL)
            </span>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Shop now"
              data-testid="link-modal-text"
              className="mt-1.5 w-full h-11 rounded-2xl bg-white border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
            />
          </label>
        )}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-pill btn-outline !text-xs">Cancel</button>
        <button
          onClick={() => onInsert(url.trim(), text.trim() || (hasSelection ? "" : url.trim()))}
          disabled={!url.trim()}
          data-testid="link-modal-insert"
          className="btn-pill btn-primary !text-xs disabled:opacity-40"
        >
          Insert
        </button>
      </div>
    </ModalShell>
  );
}

// ============ CTA modal ============
function CTAModal({ onClose, onInsert }) {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("Shop now");
  return (
    <ModalShell onClose={onClose} title="Insert call-to-action button">
      <div className="space-y-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Button label</span>
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Shop now"
            data-testid="cta-modal-label"
            className="mt-1.5 w-full h-11 rounded-2xl bg-white border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Destination URL</span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://malekdridihairandbeauty.com/shop"
            data-testid="cta-modal-url"
            className="mt-1.5 w-full h-11 rounded-2xl bg-white border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
          />
        </label>
        <div className="mt-2 flex items-center justify-center py-3 bg-[hsl(var(--blush)/0.2)] rounded-2xl">
          <span className="inline-block px-6 py-2.5 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] text-sm">
            {label || "Button label"}
          </span>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-pill btn-outline !text-xs">Cancel</button>
        <button
          onClick={() => onInsert(url.trim(), label.trim())}
          disabled={!url.trim() || !label.trim()}
          data-testid="cta-modal-insert"
          className="btn-pill btn-primary !text-xs disabled:opacity-40"
        >
          Insert button
        </button>
      </div>
    </ModalShell>
  );
}

// ============ Modal shell ============
function ModalShell({ onClose, title, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="composer-modal">
      <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-6">
        <h3 className="font-display text-xl mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}
